import React, { useState } from "react";
import api from "../services/api";
import { useNavigate, Link } from "react-router-dom";
import {
  FaEnvelope,
  FaLock,
  FaUser,
  FaHeart,
  FaBookmark,
  FaStar,
  FaExclamationTriangle
} from "react-icons/fa";

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", formData);
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 sm:px-4 py-6 sm:py-8 relative overflow-hidden">

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      </div>

      {/* Page Heading */}
      <div className="w-full text-center mb-6 sm:mb-8 px-4 relative z-10">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-3 sm:mb-4">
          Join
        </h1>
        <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-pink-600 to-purple-600 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl shadow-2xl">
          <span className="text-2xl sm:text-3xl">🎬</span>
          <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white">MovieMate</span>
        </div>
      </div>

      {/* Register Card */}
      <div className="w-full max-w-5xl bg-white/10 backdrop-blur-lg shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden flex flex-col lg:flex-row relative z-10 border border-white/20">

        {/* Left Panel */}
        <div className="w-full lg:w-1/2 bg-gradient-to-br from-pink-600 via-purple-600 to-indigo-700 text-white flex flex-col justify-center items-center py-8 sm:py-10 lg:py-12 px-6 sm:px-8 relative">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative z-10 text-center space-y-6">
            <div className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4">🎭</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Join the Community!</h2>
            <p className="text-white/90 text-sm sm:text-base lg:text-lg leading-relaxed max-w-md">
              Create your account and start your cinematic journey. Discover amazing movies, build your collections, and share your reviews.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-white/80">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <FaHeart className="text-red-400 text-xs sm:text-sm" />
                <span className="text-xs sm:text-sm">Favorites</span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <FaBookmark className="text-blue-400 text-xs sm:text-sm" />
                <span className="text-xs sm:text-sm">Watchlist</span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <FaStar className="text-yellow-400 text-xs sm:text-sm" />
                <span className="text-xs sm:text-sm">Reviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Form */}
        <div className="w-full lg:w-1/2 p-6 sm:p-8 lg:p-12 bg-white/95 backdrop-blur-sm">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Create Account</h2>
              <p className="text-gray-600 text-sm sm:text-base">Fill in your details to get started</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    required
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    required
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    name="password"
                    type="password"
                    placeholder="Create a password"
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                  <FaExclamationTriangle className="text-red-500" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-semibold py-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Create Account
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-purple-600 hover:text-purple-700 font-semibold transition-colors"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;